/** Each endpoint's own words for the Playground tour (utils/playground-tour.ts):
 *  what it's for, its filters, what comes back and inputs to try. Keyed by
 *  endpoint path; one without an entry has no tour. The wording follows the
 *  API reference (docs/data/docs-api-data.ts) and the page's own form. */
export interface EndpointIntro {
  title: string;
  summary: string;
  /** What the main input is called: "question" or "query". */
  input: string;
  /** The optional filters under Request configuration, as a list. */
  filters: string;
  receive: string;
  /** Put into the main input on click (not run: a run costs credits). */
  examples: string[];
  /** Its API reference page under /docs/api-reference, if it has one. */
  docsId?: string;
}

export const ENDPOINT_INTROS: Readonly<Record<string, EndpointIntro>> = {
  '/rag': {
    title: 'Passages that answer a question',
    summary: 'Ask a question in plain language and get back the passages from articles that answer it, each with its English text, a link to the article and its publication date.',
    input: 'question',
    filters: 'dates, sites, languages and places',
    receive: 'The passages that answer your question, each with its source.',
    examples: [
      'What are the key announcements of Skanska for last 6 months?',
      'List contracts awarded to FN Herstal since 1 January 2025',
    ],
    docsId: 'rag',
  },
  '/answer': {
    title: 'A written answer, with sources',
    summary: 'Ask a question and get the answer written out, composed from the passages /rag would return. Those passages come back too, so you can check the answer against what it was written from.',
    input: 'question',
    filters: 'dates, sites and languages',
    receive: 'The answer, and the articles it was written from under Sources.',
    examples: [
      'List contracts awarded to FN Herstal since 1 January 2025',
      'What are the key announcements of Skanska for last 6 months?',
    ],
    docsId: 'answer',
  },
  '/search': {
    title: 'Whole articles for a query',
    summary: 'Match a query against the index and get whole articles back, one row each. Use AND, OR, NOT and "quoted phrases", or plain words.',
    input: 'query',
    filters: 'named entities, dates, sites, languages and places',
    receive: 'The matching articles, with the fields you picked.',
    examples: [
      '"avionic sensor" AND (thales OR safran)',
      'Financial results 2025 Airbus OR defense contracts NATO',
    ],
    docsId: 'search',
  },
  '/similar': {
    title: 'Articles similar to a text',
    summary: 'Paste a piece of text (a headline, a paragraph or a whole article) and get back the articles that read most like it. It is plain text, not a Boolean query: AND, OR and quotes are read as words.',
    input: 'query',
    filters: 'dates, countries and places',
    receive: 'The similar articles, with the fields you picked.',
    examples: [
      'Dock workers in Rotterdam walked out over automation plans at the container terminals',
      "Nestlé's announcements regarding sustainability",
    ],
    docsId: 'similar',
  },
  '/image': {
    title: 'Images for a query',
    summary: "Search the pictures articles carry. Your Boolean query is matched against each image's own text (the words read off it, its filename, caption and keywords) and, by default, the title of the article it ran in. A photo run by many outlets comes back once.",
    input: 'query',
    filters: 'dates, countries, and whether the images must show people',
    receive: 'The matching images. Click one to see the article it appeared in and what was detected in it.',
    examples: [
      'strike AND (rail OR "public transport")',
      '"avionic sensor" AND (thales OR safran)',
    ],
    docsId: 'image',
  },
};
